/** angular */
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import { filter, first, takeUntil } from 'rxjs/operators';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
/** end angular */

/** sixense */
import {
  DsLibMainMenuLink,
  DsLibMainMenuTab,
  DsLibMainMenuSectionInterface,
  DsLibMainMenuUserInterface,
  DsLibMainMenuListItem,
  DsMainMenuLogos,
  DsLibMainMenuLinkInterface,
} from '@sixense/ds-lib-main-menu';
import { DsProject, DsUserService } from '@sixense/ds-roazhon-service';
import { DsLibLanguageService } from '@sixense/ds-lib-common';
import { BydUserAccount } from '@sixense/byd-auth-service';
import { BydFeatureFlagService } from '@sixense/byd-feature-flags';
/** end sixense */

/** classes */
import { BydLocalStorage, DsSubscriptionDestroyer } from '@sixense/ds-lib-utils';
/** classes */

/** services */
import { AuthService } from '../auth/auth.service';
import { DsLoaderService } from '@core/services/ds-loader.service';
import { DsRightsManagerService } from '@shared/services/ds-rights-manager.service';

/** end services */

/** Constants */
import { PRODUCT_LOGOS } from './constants/beyond-logs.constant';
import { environment } from '@envs/environment';
import { MENU_SECTIONS_DIRECTORY_FEATFLAG } from './constants/menu-sections-directory-featflag.constant';
import { MENU_SECTIONS } from './constants/menu-sections.constant';
import { DS_DOCUMENTATION_MENU } from './constants/menu-sections/ds-documentation-menu.constant';

@Component({
  selector: 'ds-main',
  templateUrl: './main.component.html',
  styleUrls: ['./main.component.scss'],
})
export class MainComponent extends DsSubscriptionDestroyer implements OnInit {
  /**
   * Home link
   */
  public homeLink: DsLibMainMenuLink;

  /**
   * List of tabs containing sections and links
   */
  public tabs: DsLibMainMenuTab[] = [];

  /**
   * The selected tab id
   */
  public selectedTabId: number;

  /**
   * The user logged in
   */
  public currentUser: DsLibMainMenuUserInterface;

  /**
   * Product logos displayed at the bottom.
   */
  public productLogos: DsMainMenuLogos;

  /**
   * List of projects the user has access to
   */
  private _projectList: DsProject[] = [];

  /**
   * The project list as DsLibMainMenuListItem instances
   */
  private adaptedProjectsList: DsLibMainMenuListItem[];

  /**
   * Menu sections in the main menu.
   */
  private menuSections: DsLibMainMenuSectionInterface[] = [];

  /**
   * Creates an instance of MainComponent.
   *
   * @param activatedRoute ActivatedRoute
   * @param authService AuthService
   * @param router Router
   * @param languageService DsLibLanguageService
   * @param translate TranslateService
   * @param rightsManagerService Service that keeps the count of rights for the current project. Linter thinks it's not used but used in the template.
   * @param dsLoaderService DsLoaderService
   * @param userService User service
   * @param bydFeatureFlagService Feature flag
   */
  constructor(
    private activatedRoute: ActivatedRoute,
    private authService: AuthService,
    private router: Router,
    private languageService: DsLibLanguageService,
    private translate: TranslateService,
    public rightsManagerService: DsRightsManagerService,
    public dsLoaderService: DsLoaderService,
    private userService: DsUserService,
    private bydFeatureFlagService: BydFeatureFlagService
  ) {
    super();
    this.menuSections = this.bydFeatureFlagService.hasFlags('menu_contact')
      ? MENU_SECTIONS
      : MENU_SECTIONS_DIRECTORY_FEATFLAG;

    if (!this.bydFeatureFlagService.hasFlags('ged_feature')) this.menuSections.splice(3, 1);
    this.homeLink = new DsLibMainMenuLink({ title: 'Beyond InSite', url: 'home', icon: 'home', disabled: false });

    this.productLogos = new DsMainMenuLogos(PRODUCT_LOGOS);

    if (!this.languageService.getCurrentLang()) {
      this.languageService.useLoginLang();
    }
    // Listen to url changes and update selected project inside the menu if the id has sbeen changed inside the url.
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.detectProjectChange();
      });
  }

  /**
   * Get projectList.
   *
   * @returns DsProject[]
   */
  public get projectList(): DsProject[] {
    return this._projectList;
  }

  /**
   * Set projectList.
   *
   * @param projectList the project list
   */
  public set projectList(projectList: DsProject[]) {
    this._projectList = projectList;
  }

  /**
   * The menu component will display all the sections of the loaded JSON.
   * A section which has an empty title '' will be displayed only as a list of links.
   */
  ngOnInit(): void {
    const userAccount = <BydUserAccount>this.activatedRoute.snapshot.data.BydUserResolver;
    this.authService.updateLatestAccount();
    this.currentUser = {
      firstname: userAccount.account_firstname,
      lastname: userAccount.account_lastname,
      login: userAccount.account_lid,
    };

    this.patchUserLanguage(
      userAccount.account_id,
      <string>this.languageService.getStoredLanguage(),
      <string>userAccount.account_langue
    );

    this.projectList = this.activatedRoute.snapshot.data.projectListModel.models;

    this.adaptedProjectsList = <DsLibMainMenuListItem[]>this.adaptProjectList(this.projectList);
    this.translate.onLangChange.subscribe((currentLang: LangChangeEvent) => {
      this.documentationUrlByLang(currentLang.lang);
    });
    /**
     * The menu component will display all the sections of the loaded JSON.
     * A section which has an empty title '' will be displayed only as a list of links
     */
    this.tabs[0] = new DsLibMainMenuTab({
      id: 1,
      title: 'sixense',
      selectItems: this.adaptedProjectsList,
      sections: this.menuSections,
      url: 'sixense',
    });
    this.tabs[0].selectedItem = <DsLibMainMenuListItem>this.getSelectedProject();
    this.selectedTabId = <number>this.tabs[0].id;
    this.redirectToLatestProject();
    this.documentationUrlByLang();
  }

  /**
   * Redirect user to the latest project visited.
   * If no latest project visited, redirect to the first project of the project list.
   */
  public redirectToLatestProject() {
    // Second child contains information about current project id in the url.
    const activatedRouteSnapshot = this.activatedRoute.snapshot;
    const queryParamRedirectTo =
      activatedRouteSnapshot?.queryParams?.redirectTo || BydLocalStorage.getItem('redirectTo');
    BydLocalStorage.removeItem('redirectTo');
    const secondChild = activatedRouteSnapshot.firstChild?.firstChild || null;
    const urlProjectId = +secondChild?.params.gid || null;
    const latestProjectId = this.authService.getLatestProjectId();
    // If no project id is present in the url, get the latest project id from localStorage.
    const currentProjectId = urlProjectId || latestProjectId;
    let latestProject = this.adaptedProjectsList.find((project) => project.id === currentProjectId);
    if (!latestProject && this.adaptedProjectsList.length > 0) {
      latestProject = this.adaptedProjectsList[0];
    }
    if (queryParamRedirectTo) {
      this.router.navigateByUrl(queryParamRedirectTo);
    } else if (latestProject) {
      if (latestProject.id !== latestProjectId) {
        this.authService.setLatestProjectId(latestProject.id);
      }
      this.tabs[0].selectedItem = latestProject;
      if (latestProject.id !== urlProjectId) {
        this.router.navigate(['main', 'projects', latestProject.id, 'home']);
      }
    }
  }

  /**
   * Adapt DsProject list.
   *
   * @param projectList DsProject[]
   * @returns project
   */
  adaptProjectList(projectList: DsProject[]) {
    return projectList.map((project) => {
      return {
        id: project.g_id,
        name: project.g_desc,
      };
    });
  }

  /**
   * Navigate to the selected link.
   *
   * @param link DsLibMainMenuLink
   */
  menuLinkClicked(link: DsLibMainMenuLink) {
    const formattedUrl = link.url.replace('{id}', `${this.tabs[0]?.selectedItem.id}`);
    console.log('linkClicked:', link, formattedUrl);
    this.router.navigate(formattedUrl.split('/'), {
      relativeTo: this.activatedRoute,
    });
  }

  /**
   * Log the tab click.
   *
   * @param tab DsLibMainMenuTab
   */
  menuTabClicked(tab: DsLibMainMenuTab) {
    console.log('tabClicked:', tab);
  }

  /**
   * Log the home click.
   *
   * @param menuLink DsLibMainMenuLink
   */
  menuHomeClicked(menuLink: DsLibMainMenuLink) {
    console.log('HOMEClicked:', menuLink);
  }

  /**
   * Update the latest project id
   * Then navigate to the selected project.
   *
   * @param tab The clicked tab
   */
  public onSelectItemClicked(tab: DsLibMainMenuTab) {
    console.log('element selected:', tab);
    this.authService.setLatestProjectId(tab?.selectedItem.id);
    this.router.navigate(['main', 'projects', tab?.selectedItem.id.toString(), 'home']);
  }

  /**
   * Update user language in database.
   *
   * @param accountId User id
   * @param newLanguage Local storage language
   * @param userLanguage the user language stored in database
   */
  public patchUserLanguage(accountId: number, newLanguage: string, userLanguage: string) {
    if (newLanguage !== userLanguage) {
      this.languageService.useLoginLang();
      this.userService.patchLanguage(accountId, newLanguage).pipe(first()).subscribe();
    }
  }

  /**
   * Select the url to give to the
   * documentation section in apprpriate language.
   *
   * @param currentLang the current language
   */
  private documentationUrlByLang(currentLang = this.languageService.getCurrentLang()) {
    const docMenu = DS_DOCUMENTATION_MENU;
    (<DsLibMainMenuLinkInterface>docMenu.link).url =
      currentLang === 'fr' ? environment.documentation.DOC_FR_URL : environment.documentation.DOC_EN_URL;
    this.tabs[0].sections[this.tabs[0].sections.length - 1] = docMenu;
  }

  /**
   * Get the last selected url from the path.
   *
   * @returns project id from path
   */
  private getProjectIdFromUrl(): number {
    const childSnapshot = this.activatedRoute.snapshot?.firstChild;
    return +childSnapshot?.firstChild?.params?.gid;
  }

  /**
   * Get project from the projects list with id.
   *
   * @returns Returns the project where the id match,undefined otherwise.
   */
  private getSelectedProject(): DsLibMainMenuListItem | null {
    const projectIdFromUrl = this.getProjectIdFromUrl();
    return this.adaptedProjectsList?.find((item: DsLibMainMenuListItem) => item.id === projectIdFromUrl) || null;
  }

  /**
   * Updates the selected project inside the menu upon detection of project change.
   */
  private detectProjectChange() {
    if (Array.isArray(this.tabs) && !!this.tabs.length && !this.tabs[0].selectedItem) {
      this.tabs[0].selectedItem = <DsLibMainMenuListItem>this.getSelectedProject();
    }
  }
}
